import { ATTITUDES, CALENDAR_LIMITS, CONDITIONS, TRAIT_MAX, IGNORED_PER_DOCUMENT, isWebUrl, RANK, RELATION_KINDS, STATE_KINDS, TERM_MAX, TERMS_PER_ENTITY, TIME_PRECISIONS, TIME_SOURCES, TIME_UNITS } from '@dispositio/core';
import { z } from 'zod';
import { LIMITS } from './limits.js';

export const idSchema = z.uuid();
const name = z.string().trim().min(1).max(LIMITS.name);
const title = z.string().trim().min(1).max(LIMITS.title);
const color = z.string().regex(/^#[0-9a-fA-F]{6}$/, 'Farbe als #rrggbb');
const position = z.number().finite().min(-1_000_000).max(1_000_000);

// ---------------------------------------------------------------------------
// Auth
// ---------------------------------------------------------------------------

export const registerSchema = z.strictObject({
  email: z.email().max(LIMITS.email),
  password: z.string().min(LIMITS.password.min).max(LIMITS.password.max),
  /** nur bei Registrierung auf Einladung (Beta) nötig */
  inviteCode: z.string().max(100).optional(),
});
export const loginSchema = z.strictObject({
  email: z.email().max(LIMITS.email),
  password: z.string().min(1).max(LIMITS.password.max),
});

// ---------------------------------------------------------------------------
// Projekte
// ---------------------------------------------------------------------------

/**
 * `seriesId`: Serie, in der die Geschichte entsteht (ohne: die erste); `references`: Elemente früherer Episoden dieser Serie, die als
 * Referenzen in die neue Episode kommen (PLAN.md §43)
 */
export const projectCreateSchema = z.strictObject({ name, seriesId: idSchema.optional(), references: z.array(idSchema).max(LIMITS.entitiesPerProject).optional() });
/** `dismissedIssues`: IDs der ausgeblendeten Hinweise (ganze Liste); `seriesId`: in eine andere Serie verschieben */
export const projectUpdateSchema = z.strictObject({ name: name.optional(), dismissedIssues: z.array(z.string().min(1).max(200)).max(LIMITS.dismissedIssues).optional(), seriesId: idSchema.optional() });
/** Serien: Gruppen der Geschichten in der Übersicht */
export const seriesCreateSchema = z.strictObject({ name });
export const seriesUpdateSchema = z.strictObject({ name });
/** Reihenfolge der Episoden einer Serie (PLAN.md §43): jede Geschichte der Serie genau einmal */
export const seriesOrderSchema = z.strictObject({ projectIds: z.array(idSchema).min(1).max(LIMITS.projectsPerUser) });

// ---------------------------------------------------------------------------
// Felder, Typen
// ---------------------------------------------------------------------------

export const fieldTypeSchema = z.enum(['text', 'longtext', 'number', 'date', 'image']);
export const fieldValueSchema = z.union([z.string().max(LIMITS.fieldLongText), z.number().finite(), z.null()]);
export const fieldValuesSchema = z.record(z.string().min(1).max(60), fieldValueSchema);

export const fieldDefSchema = z.strictObject({
  key: z.string().regex(/^[a-z][a-zA-Z0-9_]{0,39}$/, 'Schlüssel: Kleinbuchstabe am Anfang, dann Buchstaben, Ziffern, Unterstrich'),
  label: z.string().trim().min(1).max(LIMITS.label),
  /** `ref` gibt es nur in vordefinierten Klassen; eigene Klassen lehnt der Server damit ab */
  type: z.enum([...fieldTypeSchema.options, 'ref']),
  refType: z.string().max(LIMITS.name).optional(),
  help: z.string().max(600).optional(),
  placeholder: z.string().max(600).optional(),
  options: z.array(z.string().trim().min(1).max(60)).min(2).max(12).optional(),
});

export const dynamicFieldSchema = z.strictObject({
  id: z.string().min(1).max(64),
  label: z.string().trim().min(1).max(LIMITS.label),
  type: fieldTypeSchema,
  value: fieldValueSchema,
});

export const typeCreateSchema = z.strictObject({
  name,
  category: z.enum(['object', 'place', 'normal']),
  color,
  fields: z.array(fieldDefSchema).min(1).max(LIMITS.fieldsPerType),
  titleKey: z.string().min(1).max(60),
  hoverKey: z.string().min(1).max(60).nullable(),
  imageKey: z.string().min(1).max(60).nullable(),
  /** Nodes dieses Typs haben Textdokumente (nur für normale Typen) */
  textual: z.boolean().optional(),
  /** Standardbild der Klasse (Asset-ID des Projekts) */
  placeholderId: idSchema.nullable().optional(),
});
/** Vordefinierte Typen erlauben nur die Farbe; das prüft der Server. */
export const typeUpdateSchema = typeCreateSchema.partial().strict();

// ---------------------------------------------------------------------------
// Bibliothekseinträge (Entities)
// ---------------------------------------------------------------------------

export const refKeySchema = z.string().regex(/^[a-z][a-z0-9_]{0,29}$/, 'Verweisschlüssel: Kleinbuchstaben, Ziffern, Unterstrich, mit Buchstabe am Anfang (höchstens 30 Zeichen)');

/** Vorkommen: Wörter, mit denen ein Element im Text vorkommt (die ganze Liste; geprüft wird mit `checkTerm`) */
const termsSchema = z.array(z.string().max(TERM_MAX * 2)).max(TERMS_PER_ENTITY);

/** Haltung je Richtung einer Beziehung (AP-30) */
const attitudeSchema = z.strictObject({ source: z.enum(ATTITUDES).optional(), target: z.enum(ATTITUDES).optional() });

/** Beziehung, die von einem Element ausgeht (core relations.ts); Ziel und Grenzen prüft der Server */
export const relationSchema = z.strictObject({
  id: z.string().min(1).max(64),
  kind: z.enum(RELATION_KINDS),
  targetId: idSchema,
  from: idSchema.nullable().optional(),
  to: idSchema.nullable().optional(),
  forms: z.strictObject({ source: z.enum(['w', 'm']).optional(), target: z.enum(['w', 'm']).optional() }).optional(),
  attitude: attitudeSchema.optional(),
  turns: z.array(z.strictObject({ sceneId: idSchema, attitude: attitudeSchema, note: z.string().max(200).optional() })).max(LIMITS.turnsPerRelation).optional(),
  minutes: z.number().int().min(1).max(10_000_000).optional(),
});
const relationsSchema = z.array(relationSchema).max(LIMITS.relationsPerEntity);

/** Zustandsänderung in einer Szene (core states.ts); Element und Wert prüft der Server */
export const stateChangeSchema = z.strictObject({
  id: z.string().min(1).max(64),
  entityId: idSchema,
  kind: z.enum(STATE_KINDS),
  // Merkmal: die Ausprägung als Text
  value: z.union([z.enum(CONDITIONS), idSchema, z.string().trim().min(1).max(TRAIT_MAX), z.null()]),
  trait: z.string().trim().min(1).max(TRAIT_MAX).optional(),
  from: idSchema.optional(),
  quote: z.string().max(400).optional(),
});
const changesSchema = z.array(stateChangeSchema).max(LIMITS.changesPerScene);

/** Material einer Szene (core material.ts, PLAN.md §36): Idee mit Text, Link mit Webadresse oder Bild dieses Projekts */
export const materialItemSchema = z.discriminatedUnion('kind', [
  z.strictObject({ id: z.string().min(1).max(64), kind: z.literal('note'), title: z.string().max(LIMITS.title), text: z.string().max(LIMITS.documentText) }),
  z.strictObject({
    id: z.string().min(1).max(64),
    kind: z.literal('link'),
    title: z.string().max(LIMITS.title),
    url: z.string().max(LIMITS.materialUrl).refine(isWebUrl, 'Ein Link braucht eine Webadresse (http oder https).'),
  }),
  z.strictObject({ id: z.string().min(1).max(64), kind: z.literal('image'), title: z.string().max(LIMITS.title), assetId: idSchema }),
]);
const materialSchema = z.array(materialItemSchema).max(LIMITS.materialPerScene);

/** Beat-Listen am Manuskript (core beats.ts, PLAN.md §42); den Rest (Szenen, Figur, doppelte IDs) prüft `checkBeatSheets` */
export const beatSchema = z.strictObject({
  id: z.string().min(1).max(64),
  title: z.string().trim().min(1).max(LIMITS.title),
  description: z.string().max(LIMITS.beatDescription).optional(),
  target: z.number().finite().min(0).max(1).optional(),
  act: z.boolean().optional(),
  sceneIds: z.array(idSchema).max(LIMITS.scenesPerBeat),
});
export const beatSheetSchema = z.strictObject({
  id: z.string().min(1).max(64),
  title: z.string().trim().min(1).max(LIMITS.title),
  template: z.string().min(1).max(40).optional(),
  entityId: idSchema.nullable().optional(),
  beats: z.array(beatSchema).max(LIMITS.beatsPerSheet),
});
const beatSheetsSchema = z.array(beatSheetSchema).max(LIMITS.beatSheets);
/** Eigene Vorlage für Beat-Listen (am Konto, für alle Geschichten): ihre Beats ohne Szenen; gleicher Name ersetzt die vorhandene */
export const beatTemplateSaveSchema = z.strictObject({
  name,
  beats: z
    .array(
      z.strictObject({
        title: z.string().trim().min(1).max(LIMITS.title),
        description: z.string().max(LIMITS.beatDescription).default(''),
        target: z.number().finite().min(0).max(1).optional(),
        act: z.boolean().optional(),
      }),
    )
    .max(LIMITS.beatsPerSheet),
});
/** Wertverschiebung einer Szene: −2 (stark abwärts) bis +2 (stark aufwärts) */
const shiftSchema = z.number().int().min(-2).max(2);

/** Eigener Kalender der Geschichte (core calendar.ts, PF 16); den Rest prüft `checkCalendar` */
export const calendarSchema = z.strictObject({
  name: z.string().max(CALENDAR_LIMITS.name),
  months: z
    .array(z.strictObject({ name: z.string().min(1).max(CALENDAR_LIMITS.name), days: z.number().int().min(1).max(CALENDAR_LIMITS.monthDays) }))
    .min(1)
    .max(CALENDAR_LIMITS.months),
  weekdays: z.array(z.string().min(1).max(CALENDAR_LIMITS.name)).max(CALENDAR_LIMITS.weekdays),
  firstWeekday: z.number().int().min(0).max(CALENDAR_LIMITS.weekdays),
  era: z.string().max(CALENDAR_LIMITS.era),
  leap: z.strictObject({ every: z.number().int().min(1).max(CALENDAR_LIMITS.leapEvery), month: z.number().int().min(1).max(CALENDAR_LIMITS.months) }).nullable().optional(),
});

/** Eingabezeile in natürlicher Sprache: die Zeile und wo sie steht (Element bzw. Szene) */
export const statementSchema = z.strictObject({ text: z.string().trim().min(1).max(300), subjectId: idSchema.nullable().optional(), sceneId: idSchema.nullable().optional() });

export const entityCreateSchema = z.strictObject({ typeId: idSchema, fields: fieldValuesSchema.optional(), refKey: refKeySchema.optional(), terms: termsSchema.optional(), x: position.optional(), y: position.optional() });
/** `x`/`y`: Lage auf der Zeichenfläche der Bibliothek (null: noch nicht platziert) */
export const entityUpdateSchema = z
  .strictObject({ fields: fieldValuesSchema.optional(), refKey: refKeySchema.optional(), terms: termsSchema.optional(), relations: relationsSchema.optional(), x: position.nullable().optional(), y: position.nullable().optional() })
  .refine((v) => v.fields !== undefined || v.refKey !== undefined || v.terms !== undefined || v.relations !== undefined || v.x !== undefined || v.y !== undefined, 'Nichts zu ändern');
/** Einträge der Bibliothek einer anderen eigenen Geschichte als Kopien übernehmen */
export const libraryImportSchema = z.strictObject({ sourceProjectId: idSchema, entityIds: z.array(idSchema).min(1).max(LIMITS.entitiesPerProject) });

// ---------------------------------------------------------------------------
// Gruppierungsrahmen der Bibliothek
// ---------------------------------------------------------------------------

const extent = z.number().finite().min(40).max(1_000_000);
/** Sortierschlüssel eines Kapitels, einer Szene bzw. einer Kategorie (siehe `rankBetween`) */
export const rankSchema = z.string().regex(RANK, 'Sortierschlüssel: Ziffern und Kleinbuchstaben, nicht auf 0 endend');
/** `typeId`: Bereich der Bibliothek (Klasse); fehlt er, gehört die Gruppe zu Person. `rank`: Stelle in der Kartenansicht (ohne: hinten) */
export const groupCreateSchema = z.strictObject({ name, typeId: idSchema.optional(), color: color.optional(), x: position, y: position, w: extent.optional(), h: extent.optional(), rank: rankSchema.optional() });
export const groupUpdateSchema = z
  .strictObject({ name: name.optional(), color: color.optional(), x: position.optional(), y: position.optional(), w: extent.optional(), h: extent.optional(), rank: rankSchema.optional() })
  .refine((v) => Object.keys(v).length > 0, 'Nichts zu ändern');

// ---------------------------------------------------------------------------
// Nodes
// ---------------------------------------------------------------------------

/** Zeitwert einer Szene als Text: Datum `JJJJ-MM-TTThh:mm` oder eigene Zählung („Tag 12, 06:00“); geprüft wird er im Dienst */
const timeValue = z.string().trim().max(LIMITS.label).nullable();
/** Zeit einer Szene (PLAN.md §15): Beginn, Ende (Zeiträume), Genauigkeit, ungefähr, Herkunft; null = keine */
export const sceneTimeSchema = z.strictObject({
  start: timeValue,
  end: timeValue,
  precision: z.enum(TIME_PRECISIONS).optional(),
  approx: z.boolean().optional(),
  source: z.enum(TIME_SOURCES).optional(),
  /** Dauer statt eines Endes (`3d`, `2h`): das Ende ist dann Beginn + Dauer */
  duration: z.string().trim().max(20).optional(),
  /** relativ zu einer anderen Szene: ihre Id, von Hand mit festem Abstand (`+3d`) */
  anchor: z.strictObject({ scene: idSchema, offset: z.string().trim().max(40).optional() }).optional(),
});

export const nodeCreateSchema = z.discriminatedUnion('mode', [
  /** Instanz eines vorhandenen Bibliothekseintrags (Ziehen aus der Seitenleiste) */
  z.strictObject({ mode: z.literal('instance'), entityId: idSchema, parentId: idSchema, x: position, y: position }),
  /** Neuer normaler Node (z. B. Handlung) */
  z.strictObject({ mode: z.literal('normal'), typeId: idSchema, parentId: idSchema, x: position, y: position, fields: fieldValuesSchema.optional() }),
  /** Kopie eines normalen Nodes, einer Instanz oder einer Szene (die Kopie einer Szene ist nicht eingeordnet) */
  z.strictObject({ mode: z.literal('copy'), sourceId: idSchema, parentId: idSchema.optional(), x: position.optional(), y: position.optional() }),
  /** Weitere Zeitlinie: waagerecht bündig mit der Hauptzeitlinie, senkrecht bei `y` (ohne: unter den vorhandenen) */
  z.strictObject({ mode: z.literal('timeline'), title: title.optional(), y: position.optional() }),
  /** Szene als Zeitpunkt („Ereignis“), am Zeitstrahl angedockt */
  z.strictObject({ mode: z.literal('event'), timelineId: idSchema, at: position, fields: fieldValuesSchema.optional() }),
  /** Szene als Zeitraum, am Zeitstrahl angedockt */
  z.strictObject({ mode: z.literal('period'), timelineId: idSchema, from: position, length: z.number().finite().min(1).max(1_000_000).optional(), fields: fieldValuesSchema.optional() }),
  /** Lose Szene (Zeitpunkt bzw. Zeitraum) auf der Fläche eines Nodes (später am Zeitstrahl andockbar) */
  z.strictObject({ mode: z.literal('loose'), kind: z.enum(['event', 'period']), parentId: idSchema, x: position, y: position, fields: fieldValuesSchema.optional(), time: sceneTimeSchema.nullable().optional() }),
  /** Abschnitt im Manuskript (zwischen dessen Kapiteln und Abschnitten); ohne `rank` ans Ende */
  z.strictObject({ mode: z.literal('section'), rank: rankSchema.optional(), fields: fieldValuesSchema.optional() }),
  /** Kapitel im Manuskript bzw. im Abschnitt `sectionId` (ohne bzw. null: direkt im Manuskript); ohne `rank` ans Ende */
  z.strictObject({ mode: z.literal('chapter'), sectionId: idSchema.nullable().optional(), rank: rankSchema.optional(), fields: fieldValuesSchema.optional() }),
  /** Szene, die (noch) nicht auf der Fläche liegt: in einem Kapitel (ohne `rank` ans Ende) oder nicht eingeordnet (`chapterId: null`) */
  z.strictObject({ mode: z.literal('scene'), chapterId: idSchema.nullable(), rank: rankSchema.optional(), kind: z.enum(['event', 'period']).optional(), fields: fieldValuesSchema.optional(), time: sceneTimeSchema.nullable().optional() }),
]);

export const flagSchema = z.strictObject({ id: z.string().min(1).max(64), pos: position, label: z.string().max(LIMITS.label), dy: z.number().finite().min(-100_000).max(100_000).optional() });

export const timeDisplaySchema = z.strictObject({ year: z.boolean(), month: z.boolean(), day: z.boolean(), time: z.boolean() });

/** Eigene Zählung eines Zeitstrahls: Start und Ende als Zahl in Minuten, Tagen oder Jahren, optional mit Nullpunkt (Datum) */
export const reckoningSchema = z.strictObject({
  unit: z.enum(TIME_UNITS),
  start: z.number().finite().min(-1e12).max(1e12),
  end: z.number().finite().min(-1e12).max(1e12),
  zero: z.string().max(LIMITS.label).nullable().optional(),
});

export const timelinePatchSchema = z.strictObject({
  startPos: position.optional(),
  endPos: position.optional(),
  startLabel: z.string().max(LIMITS.label).optional(),
  endLabel: z.string().max(LIMITS.label).optional(),
  flags: z.array(flagSchema).max(LIMITS.flagsPerTimeline).optional(),
  display: timeDisplaySchema.optional(),
  /** eigene Zählung statt Datum und Uhrzeit (null: wieder Datum und Uhrzeit) */
  reckoning: reckoningSchema.nullable().optional(),
  /** an die Hauptzeitlinie koppeln bzw. entkoppeln (nur weitere Zeitlinien) */
  coupled: z.boolean().optional(),
});

/** Neues Textdokument (.md) an einer Szene (bzw. einem Node mit Texten) */
export const documentCreateSchema = z.strictObject({ name: z.string().trim().min(1).max(LIMITS.documentName), text: z.string().max(LIMITS.documentText).optional() });
/** Bewusst nicht verknüpfte Stelle: Vorkommen mit dem Wort davor und danach */
const ignoredTermSchema = z.strictObject({ term: z.string().min(1).max(TERM_MAX), before: z.string().max(TERM_MAX), after: z.string().max(TERM_MAX) });
/**
 * Umbenennen (nur frei angelegte Dokumente), Text ändern und/oder die bewusst nicht verknüpften Stellen setzen (ganze Liste).
 * `base`: Prüfsumme (core `textHash`) des Texts, auf dem die Änderung aufbaut – stimmt sie nicht mehr, lehnt der Server ab (Konflikt).
 */
export const documentUpdateSchema = z
  .strictObject({
    name: z.string().trim().min(1).max(LIMITS.documentName).optional(),
    text: z.string().max(LIMITS.documentText).optional(),
    base: z.string().max(32).optional(),
    ignored: z.array(ignoredTermSchema).max(IGNORED_PER_DOCUMENT).optional(),
  })
  .refine((v) => v.name !== undefined || v.text !== undefined || v.ignored !== undefined, 'Nichts zu ändern');

/**
 * Version eines Textdokuments sichern (PLAN.md §28): mit Titel (leer: ohne) und dem Text, wie er gerade im Editor steht (ohne: der
 * gespeicherte Text).
 */
export const snapshotCreateSchema = z.strictObject({ title: z.string().trim().max(LIMITS.snapshotTitle).optional(), text: z.string().max(LIMITS.documentText).optional() });
/** Version umbenennen */
export const snapshotUpdateSchema = z.strictObject({ title: z.string().trim().max(LIMITS.snapshotTitle) });

export const nodeUpdateSchema = z.strictObject({
  fields: fieldValuesSchema.optional(),
  dynamicFields: z.array(dynamicFieldSchema).max(LIMITS.dynamicFieldsPerNode).optional(),
  tagColor: color.nullable().optional(),
  /** Abstand der Beschriftungsbox einer angedockten Szene zum Element (y) */
  labelDy: z.number().finite().min(-100_000).max(100_000).nullable().optional(),
  /** Zeiten und Bild neben/unter der Beschriftungsbox einer angedockten Szene */
  labelShow: z.strictObject({ time: z.boolean().optional(), content: z.boolean().optional(), description: z.boolean().optional(), collapsed: z.boolean().optional(), image: z.boolean().optional() }).nullable().optional(),
  /**
   * Node in einen anderen Node verschieben. Ziel Zeitstrahl: andocken (`at` bzw. `from`/`to`), sonst lose ablegen (`x`/`y`).
   * Szenen lösen sich so auch wieder vom Zeitstrahl; Ziel Manuskript: die Szene liegt nicht mehr auf der Fläche.
   */
  parentId: idSchema.optional(),
  x: position.optional(),
  y: position.optional(),
  /** Notizzettel: Größe auf der Fläche (null = Standardgröße) */
  w: extent.nullable().optional(),
  h: extent.nullable().optional(),
  at: position.optional(),
  from: position.optional(),
  to: position.optional(),
  timeline: timelinePatchSchema.optional(),
  /** Szene: ihre Zeit (null = keine); angedockt an einen Zeitstrahl mit Zeitrechnung rückt sie an diese Zeit */
  time: sceneTimeSchema.nullable().optional(),
  /** Abschnitt: neue Stelle im Manuskript; Kapitel: Stelle im Manuskript bzw. Abschnitt (mit `sectionId` in einem anderen); Szene: Stelle in ihrem Kapitel (mit `chapterId` in einem anderen) */
  rank: rankSchema.optional(),
  /** Kapitel: Abschnitt, in dem es steht (null = direkt im Manuskript; ohne `rank` ans Ende) */
  sectionId: idSchema.nullable().optional(),
  /** Szene: Kapitel im Manuskript (null = nicht eingeordnet; ohne `rank` ans Ende des Kapitels) */
  chapterId: idSchema.nullable().optional(),
  /** Szene: bewusst nicht erzählt (kein Hinweis „nicht eingeordnet“) */
  narrateSkip: z.boolean().optional(),
  /** Szene: was sich in ihr ändert (ganze Liste) */
  changes: changesSchema.optional(),
  /** Szene: ihr Material (ganze Liste) */
  material: materialSchema.optional(),
  /** Manuskript: seine Beat-Listen (ganze Liste) */
  beatSheets: beatSheetsSchema.optional(),
  /** Szene: ihre Wertverschiebung (null = keine Angabe) */
  shift: shiftSchema.nullable().optional(),
  /** Wurzel der Geschichte: ihr eigener Kalender (null = gregorianisch) */
  calendar: calendarSchema.nullable().optional(),
});

// ---------------------------------------------------------------------------
// Verbindungen, Bilder
// ---------------------------------------------------------------------------

export const connectionCreateSchema = z.strictObject({
  sourceId: idSchema,
  targetId: idSchema,
  visible: z.boolean().default(true),
  label: z.string().trim().max(LIMITS.label).default(''),
});
export const connectionUpdateSchema = z.strictObject({
  visible: z.boolean().optional(),
  label: z.string().trim().max(LIMITS.label).optional(),
});

export const assetUploadSchema = z.strictObject({
  dataUrl: z.string().regex(/^data:image\/(jpeg|png|webp);base64,[A-Za-z0-9+/=]+$/, 'Erlaubt sind JPEG, PNG und WebP').max(Math.ceil(LIMITS.assetBytes * 1.4)),
});

// ---------------------------------------------------------------------------
// Projektordner
// ---------------------------------------------------------------------------

/** Name eines Ordners unterhalb des Projektverzeichnisses: Kleinbuchstaben, Ziffern, Punkt, Bindestrich, Unterstrich. */
export const folderNameSchema = z.string().regex(/^[a-z0-9][a-z0-9._-]{0,59}$/, 'Ordnername: Kleinbuchstaben, Ziffern, Punkt, Bindestrich, Unterstrich');
export const folderImportSchema = z.strictObject({ folder: folderNameSchema });
export const folderExportSchema = z.strictObject({ folder: folderNameSchema.optional() });

// ---------------------------------------------------------------------------
// Verlauf (Rückgängig / Wiederholen)
// ---------------------------------------------------------------------------

const documentRecordSchema = z.strictObject({
  id: z.string().min(1).max(64),
  name: z.string().trim().min(1).max(LIMITS.documentName),
  text: z.string().max(LIMITS.documentText),
  locked: z.boolean(),
  ignored: z.array(ignoredTermSchema).max(IGNORED_PER_DOCUMENT).optional(),
});

const timelineRecordSchema = z.strictObject({
  startPos: position,
  endPos: position,
  startLabel: z.string().max(LIMITS.label),
  endLabel: z.string().max(LIMITS.label),
  flags: z.array(flagSchema).max(LIMITS.flagsPerTimeline),
  display: timeDisplaySchema.optional(),
  reckoning: reckoningSchema.nullable().optional(),
  coupled: z.boolean().optional(),
});

/** Ein Node, wie er in der Geschichte steht (Stand vor bzw. nach einem Schritt) */
/** Farbplatz des automatischen Bilds (core autoImage.ts) */
const colorSlotSchema = z.number().int().min(0).max(999_999);
export const nodeRecordSchema = z.strictObject({
  id: idSchema,
  parentId: idSchema.nullable(),
  typeId: idSchema,
  kind: z.enum(['story', 'timeline', 'event', 'period', 'instance', 'normal', 'manuscript', 'section', 'chapter', 'scene']),
  entityId: idSchema.nullable(),
  fields: fieldValuesSchema,
  dynamicFields: z.array(dynamicFieldSchema).max(LIMITS.dynamicFieldsPerNode),
  documents: z.array(documentRecordSchema).max(LIMITS.documentsPerNode),
  tagColor: color.nullable(),
  labelDy: z.number().finite().min(-100_000).max(100_000).nullable().optional(),
  labelShow: z.strictObject({ time: z.boolean().optional(), content: z.boolean().optional(), description: z.boolean().optional(), collapsed: z.boolean().optional(), image: z.boolean().optional() }).nullable().optional(),
  x: position.nullable(),
  y: position.nullable(),
  w: extent.nullable().optional(),
  h: extent.nullable().optional(),
  at: position.nullable(),
  from: position.nullable(),
  to: position.nullable(),
  timeline: timelineRecordSchema.nullable(),
  time: sceneTimeSchema.nullable().optional(),
  rank: rankSchema.nullable().optional(),
  chapterId: idSchema.nullable().optional(),
  narrates: z.array(z.strictObject({ nodeId: idSchema, mode: z.string().min(1).max(20) })).max(200).optional(),
  narrateSkip: z.boolean().optional(),
  changes: changesSchema.optional(),
  material: materialSchema.optional(),
  beatSheets: beatSheetsSchema.optional(),
  shift: shiftSchema.nullable().optional(),
  colorSlot: colorSlotSchema.optional(),
  calendar: calendarSchema.nullable().optional(),
  createdAt: z.string().min(1).max(40),
});
export const entityRecordSchema = z.strictObject({
  id: idSchema,
  typeId: idSchema,
  fields: fieldValuesSchema,
  refKey: refKeySchema,
  terms: termsSchema,
  learned: termsSchema.optional(),
  aliases: z.array(z.string().max(60)).max(50).optional(),
  relations: relationsSchema.optional(),
  x: position.nullable(),
  y: position.nullable(),
  colorSlot: colorSlotSchema.optional(),
});
export const groupRecordSchema = z.strictObject({ id: idSchema, typeId: idSchema.nullable(), name, color, x: position, y: position, w: extent, h: extent, rank: rankSchema.nullable() });
export const connectionRecordSchema = z.strictObject({ id: idSchema, sourceId: idSchema, targetId: idSchema, visible: z.boolean(), label: z.string().max(LIMITS.label) });

type Entry = { id: string; from: { id: string } | null; to: { id: string } | null };
/** Ein Datensatz in einem Schritt: `from` = Stand, den der Server gerade haben muss, `to` = Stand danach (null: gibt es nicht) */
const entry = <T extends z.ZodType<{ id: string }>>(record: T, max: number) =>
  z
    .array(z.strictObject({ id: idSchema, from: record.nullable(), to: record.nullable() }))
    .max(max)
    .refine((list) => (list as Entry[]).every((e) => (e.from === null || e.from.id === e.id) && (e.to === null || e.to.id === e.id)), 'Datensatz passt nicht zu seiner ID')
    .refine((list) => new Set((list as Entry[]).map((e) => e.id)).size === list.length, 'Jeder Datensatz nur einmal');

/**
 * Rückgängig bzw. Wiederholen: Der Server prüft, dass die Geschichte bei allen genannten Datensätzen dem Stand `from` entspricht, und
 * setzt sie dann auf `to` – mit denselben IDs, ohne Nebenwirkungen (keine automatisch angelegten Kopien, kein Wachsen von Zeitstrahlen).
 */
export const historyStepSchema = z.strictObject({
  nodes: entry(nodeRecordSchema, LIMITS.nodesPerProject),
  entities: entry(entityRecordSchema, LIMITS.entitiesPerProject),
  groups: entry(groupRecordSchema, LIMITS.groupsPerProject),
  connections: entry(connectionRecordSchema, LIMITS.connectionsPerProject),
});
